import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Paper, Chip, CircularProgress, Collapse, IconButton
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import API_ENDPOINTS from '../config/apiConfig';
import { useTranslation } from '../i18n/i18n';
import { getWeekDays, formatDayLabel, formatWeekRange } from '../utils/dateUtils';

export default function TimecardSummary({ userEmail }) {
  const { t, lang } = useTranslation();
  const [timecards, setTimecards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    const fetchTimecards = async () => {
      setLoading(true);
      try {
        const res = await fetch(API_ENDPOINTS.TIMECARD_LIST(userEmail), { credentials: 'include' });
        if (res.ok) {
          const data = await res.json();
          setTimecards(data.timecards || []);
        }
      } catch (err) {
        console.error('Error fetching timecards:', err);
      } finally {
        setLoading(false);
      }
    };
    if (userEmail) fetchTimecards();
  }, [userEmail]);

  const statusColor = { draft: 'default', submitted: 'warning', approved: 'success', rejected: 'error' };

  if (loading) {
    return <Box sx={{ textAlign: 'center', p: 4 }}><CircularProgress /></Box>;
  }

  if (timecards.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', p: 4 }}>
        <Typography variant="h6">{t('calendar_history_empty')}</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <TableContainer component={Paper} variant="outlined">
        <Table size="small" sx={{ minWidth: 560 }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600 }}>{t('calendar_nav_previous').split(' ')[0] === 'Semana' ? 'Semana' : 'Week'}</TableCell>
              <TableCell align="right" sx={{ fontWeight: 600 }}>{t('calendar_total_hours')}</TableCell>
              <TableCell align="right" sx={{ fontWeight: 600 }}>{t('status')}</TableCell>
              <TableCell align="right" sx={{ fontWeight: 600 }}>{t('calendar_notes_label')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {timecards.map((tc) => (
              <React.Fragment key={tc.week_start}>
                <TableRow
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => setExpanded(expanded === tc.week_start ? null : tc.week_start)}
                >
                  <TableCell>{formatWeekRange(tc.week_start, lang)}</TableCell>
                  <TableCell align="right">{tc.total_hours}</TableCell>
                  <TableCell align="right">
                    <Chip label={t(`calendar_status_${tc.status}`)} color={statusColor[tc.status] || 'default'} size="small" />
                  </TableCell>
                  <TableCell align="right">
                    <IconButton size="small">
                      {expanded === tc.week_start ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell colSpan={4} sx={{ py: 0 }}>
                    <Collapse in={expanded === tc.week_start}>
                      <Box sx={{ py: 2, px: 1 }}>
                        {tc.rejection_reason && (
                          <Typography variant="body2" color="error" sx={{ mb: 1 }}>
                            {t('calendar_rejection_reason')}: {tc.rejection_reason}
                          </Typography>
                        )}
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                          {getWeekDays(tc.week_start).map((d) => {
                            const dayData = (tc.days || {})[d] || {};
                            return (
                              <Chip
                                key={d}
                                label={`${formatDayLabel(d, lang)}: ${dayData.hours || 0}h`}
                                variant="outlined"
                                size="small"
                              />
                            );
                          })}
                        </Box>
                      </Box>
                    </Collapse>
                  </TableCell>
                </TableRow>
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
